import { useEffect } from "react";
import { useObservable, useSelector } from "@legendapp/state/react";
import {
  chatSuggestionsService,
  type ChatGreeting,
  type PersonalizedSuggestion,
} from "@/services/chatSuggestionsService";
import { contentPrefetchService } from "@/services/contentPrefetchService";

type UserIdentity = {
  id?: string | null;
  email?: string | null;
} | null | undefined;

const getUserDisplayName = (email?: string | null): string | undefined => {
  if (!email) return undefined;
  const username = email.split("@")[0];
  if (!username) return undefined;
  const namePart = username.split(/[._-]/)[0];
  if (!namePart) return username;
  return namePart.charAt(0).toUpperCase() + namePart.slice(1).toLowerCase();
};

export function useChatSuggestions(user: UserIdentity) {
  const initialCachedContent = contentPrefetchService.getCachedChatContent();
  const personalizedSuggestions$ = useObservable<PersonalizedSuggestion[]>(
    initialCachedContent?.suggestions ?? chatSuggestionsService.getDefaultSuggestions()
  );
  const chatGreeting$ = useObservable<ChatGreeting>(
    initialCachedContent?.greeting ?? chatSuggestionsService.getDefaultGreeting()
  );
  const suggestionsLoading$ = useObservable<boolean>(
    !initialCachedContent
  );

  const personalizedSuggestions = useSelector(personalizedSuggestions$);
  const chatGreeting = useSelector(chatGreeting$);
  const suggestionsLoading = useSelector(suggestionsLoading$);

  useEffect(() => {
    if (!user?.id) {
      suggestionsLoading$.set(false);
      return;
    }

    const userName = getUserDisplayName(user.email);
    contentPrefetchService.setUser(user.id, userName);

    const cachedContent = contentPrefetchService.getCachedChatContent();
    if (cachedContent) {
      personalizedSuggestions$.set(cachedContent.suggestions);
      chatGreeting$.set(cachedContent.greeting);
      suggestionsLoading$.set(false);
      contentPrefetchService.prefetchChatContent(true);
      return;
    }

    suggestionsLoading$.set(true);
    contentPrefetchService
      .prefetchChatContent()
      .then((content) => {
        personalizedSuggestions$.set(content.suggestions);
        chatGreeting$.set(content.greeting);
        suggestionsLoading$.set(false);
      })
      .catch(() => {
        suggestionsLoading$.set(false);
      });

    const unsubscribe = contentPrefetchService.onContentUpdate("chat", (content) => {
      personalizedSuggestions$.set(content.suggestions);
      chatGreeting$.set(content.greeting);
    });

    return unsubscribe;
  }, [user?.id, user?.email, personalizedSuggestions$, chatGreeting$, suggestionsLoading$]);

  return { personalizedSuggestions, chatGreeting, suggestionsLoading };
}
